/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import i18next from 'i18next'
import { produce } from 'immer'
import insertTextAtCursor from 'insert-text-at-cursor'
import { defer, dropRight, throttle, truncate } from 'lodash'
import { Corner, getNodeAtPath, getOtherDirection, getPathToCorner, updateTree } from 'react-mosaic-component'
import * as shortid from 'shortid'
// import UndoManager from 'undo-manager'
import v from 'voca'
import { updateTreeNode } from '../backend/treeNode/updateData'
import { showToastByMenuDirectionBase } from '../components/commonInterface/toast'
import { getNodeKey } from '../components/list/window1/functions'
import { getIdForRenderTileInJSON } from '../components/list/window2/functions'
import { NodeById } from './classes'
import { decoratingConfig, sidebarDirection } from './configs'
import { globalOnDemandVars } from './context'
import { ctrlOrCmd } from './useEnvironment'
import { changeNodeAtPath, getNodeAtPath as getNodeAtPathForTree } from 'react-sortable-tree'

class UndoManager {
  constructor () {
    this.waitingList = []
    this.usedList = []
  }

  clear () {
    this.waitingList = []
    this.usedList = []
  }

  undoClear () {
    this.waitingList = []
  }

  redoClear () {
    this.usedList = []
  }

  add (pair) {
    if (process.env.NODE_ENV !== 'production') {
      console.tron.log('undoredo / add')
    }

    this.waitingList.push(pair)

    this.redoClear()
  }

  undo () {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.tron.log('undoredo / undo 0.1')
      }
      if (this.waitingList.length) {
        if (process.env.NODE_ENV !== 'production') {
          console.tron.log('undoredo / undo 0.2')
        }
        const pair = this.waitingList.pop()

        if (process.env.NODE_ENV !== 'production') {
          console.tron.display({
            name: 'undoredo / undo 0.3',
            value: {
              waitingList: this.waitingList,
              usedList: this.usedList
            }
            // , important: true
          })
        }

        this.usedList.push(pair)

        if (process.env.NODE_ENV !== 'production') {
          console.tron.display({
            name: 'undoredo / undo 0.4',
            value: {
              waitingList: this.waitingList,
              usedList: this.usedList
            }
            // , important: true
          })
        }

        pair.undo()
        if (process.env.NODE_ENV !== 'production') {
          console.tron.log('undoredo / undo 0.5')
        }
      }
    } catch (e) {
      this.undoClear()
    }
  }
  redo () {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.tron.display({
          name: 'undoredo / redo 0.1',
          value: {
            waitingList: this.waitingList,
            usedList: this.usedList
          }
          // , important: true
        })
      }
      if (this.usedList.length) {
        if (process.env.NODE_ENV !== 'production') {
          console.tron.log('undoredo / redo 0.2')
        }
        const pair = this.usedList.pop()
        if (process.env.NODE_ENV !== 'production') {
          console.tron.display({
            name: 'undoredo / redo 0.3',
            value: {
              waitingList: this.waitingList,
              usedList: this.usedList
            }
            // , important: true
          })
        }

        this.waitingList.push(pair)

        if (process.env.NODE_ENV !== 'production') {
          console.tron.display({
            name: 'undoredo / redo 0.4',
            value: {
              waitingList: this.waitingList,
              usedList: this.usedList
            }
            // , important: true
          })
        }

        pair.redo()
        if (process.env.NODE_ENV !== 'production') {
          console.tron.log('undoredo / redo 0.5')
        }
      }
    } catch (e) {
      this.redoClear()
    }
  }
}

export const undoManager = new UndoManager()

// export function registerToUndoManagerForMosaicValue () {
//   const getMosaicValue = globalOnDemandVars.isPixelMode() ? globalOnDemandVars.mosaicValueForPixelMode : globalOnDemandVars.mosaicValue
//   const setMosaicValue = globalOnDemandVars.isPixelMode() ? globalOnDemandVars.set_mosaicValueForPixelMode : globalOnDemandVars.set_mosaicValue
//
//   const prevMosaicValue = getMosaicValue()
//   defer(() => {
//     const nextMosaicValue = getMosaicValue()
//
//     undoManager.add({
//       redo: () => {
//         try {
//           setMosaicValue(nextMosaicValue)
//           globalOnDemandVars.updateWindow2InitialValue()()
//         } catch (e) {
//           undoManager.clear()
//         }
//       },
//       undo: () => {
//         try {
//           setMosaicValue(prevMosaicValue)
//           globalOnDemandVars.updateWindow2InitialValue()()
//         } catch (e) {
//           undoManager.clear()
//         }
//       }
//     })
//   })
// }

export const logicSymbolsForParsing = [
  ['\\ne', '≠'], ['\\approx', '≒'], ['\\neg', '￢'], ['\\to', '→'], ['\\gets', '←'], ['\\iff', '↔'],
  ['\\implies', '⇒'], ['\\logeq', '⇔'], ['\\all', '∀'], ['\\exist', '∃'], ['\\and', '∧'], ['\\or', '∨'],
  ['\\therefore', '∴'], ['\\because', '∵'], ['\\in', '∈'], ['\\ni', '∋'], ['\\subseteq', '⊆'], ['\\supseteq', '⊇'],
  ['\\subset', '⊂'], ['\\supset', '⊃'], ['\\union', '∪'], ['\\intersection', '∩'], ['\\le', '≤'], ['\\geq', '≥'],
  ['\\ll', '≪'], ['\\gg', '≫'], ['\\pm', '±'], ['\\div', '÷'], ['\\times', '×'], ['∈f', '∞']
]
export const logicSymbols2 = [
  '+', '-', '=', '/', '*', '~', '!', '@', '#', '$', '%', '^', '&', '_', '\\', '|', '?', '`'
]
export const logicSymbols3 = [
  '(', ')', '[', ']', '{', '}', '<', '>', ':', ';', '.', ',', '\'', '"'
]
export const greekCharactersForParsing = [
  ['\\alpha', 'α'], ['\\Alpha', 'Α'], ['\\beta', 'β'], ['\\Beta', 'B'], ['\\gamma', 'γ'], ['\\Gamma', 'Γ'],
  ['\\delta', 'δ'], ['\\Delta', 'Δ'], ['\\epsilon', 'ε'], ['\\Epsilon', 'E'], ['\\zeta', 'ζ'], ['\\Zeta', 'Z'],
  ['\\eta', 'η'], ['\\eta', 'H'], ['\\theta', 'θ'], ['\\Theta', 'Θ'], ['\\iota', 'ι'], ['\\Iota', 'I'],
  ['\\kappa', 'ϰ'], ['\\Kappa', 'K'], ['\\lambda', 'λ'], ['\\Lambda', 'Λ'], ['\\mu', 'μ'], ['\\Mu', 'M'],
  ['\\nu', 'ν'], ['\\Nu', 'N'], ['\\xi', 'ξ'], ['\\Xi', 'Ξ'], ['\\omicron', 'ℴ'], ['\\Omicron', 'O'],
  ['\\pi', 'π'], ['\\Pi', 'Π'], ['\\rho', 'ρ'], ['\\Rho', 'P'], ['\\sigma', 'σ'], ['\\Sigma', 'Σ'],
  ['\\tau', 'τ'], ['\\Tau', 'T'], ['\\upsilon', 'υ'], ['\\Upsilon', 'Υ'], ['\\phi', 'ϕ'], ['\\Phi', 'Φ'],
  ['\\chi', 'χ'], ['\\Chi', 'X'], ['\\psi', 'ψ'], ['\\Psi', 'Ψ'], ['\\omega', 'ω'], ['\\Omega', 'Ω']
]

const regexForLaTeX = /\$.*?\$/
const regexForLaTeX2 = /(.*?)(\$.*?\$)(.*)/
function parseLogicSymbolsForSimpleString (string) {
  let parsedString = string

  logicSymbolsForParsing.concat(greekCharactersForParsing).forEach(([stringForParsing, logicSymbol]) => {
    parsedString = parsedString.replace(stringForParsing, logicSymbol)
  })

  return parsedString
}

export function parseLogicSymbols (string) {
  if (regexForLaTeX.test(string)) {
    return string.replace(regexForLaTeX2, (match, p1, p2, p3) => {
      // if (process.env.NODE_ENV !== 'production') {
      //   console.tron.display({
      //     name: 'parseLogicSymbols',
      //     preview: match,
      //     value: {
      //       p1, p2, p3,
      //       parsedP1: parseLogicSymbols(p1),
      //       parsedP3: parseLogicSymbols(p3),
      //       result: parseLogicSymbols(p1) + p2 + parseLogicSymbols(p3)
      //     }
      //     // , important: true
      //   })
      // }
      return parseLogicSymbols(p1) + p2 + parseLogicSymbols(p3)
    })
  } else {
    return parseLogicSymbolsForSimpleString(string)
  }
}

export function getParsedText (text) {
  return v(text).chain().lowerCase().trim().value()
}

export function getColorByDepth (depth, lightColor) {
  let colorByDepth
  switch (depth) {
    case 1:
      if (lightColor) {
        colorByDepth = 'var(--gray13)'
      } else {
        colorByDepth = 'var(--gray12)'
      }
      break
    case 2:
      if (lightColor) {
        colorByDepth = 'var(--gray12)'
      } else {
        colorByDepth = 'var(--gray11)'
      }
      break
    case 3:
      if (lightColor) {
        colorByDepth = 'var(--gray11)'
      } else {
        colorByDepth = 'var(--gray10)'
      }
      break
    case 4:
      if (lightColor) {
        colorByDepth = 'var(--gray10)'
      } else {
        colorByDepth = 'var(--gray9)'
      }
      break
    case 5:
      if (lightColor) {
        colorByDepth = 'var(--gray9)'
      } else {
        colorByDepth = 'var(--gray8)'
      }
      break
    case 6:
      if (lightColor) {
        colorByDepth = 'var(--gray8)'
      } else {
        colorByDepth = 'var(--gray7)'
      }
      break
    default:
      if (lightColor) {
        colorByDepth = 'var(--gray7)'
      } else {
        colorByDepth = 'var(--gray6)'
      }
      break
  }

  return colorByDepth
}

export function getLightColorByDepth (depth, lightColor) {
  let colorByDepth
  switch (depth % 6) {
    case 1:
      if (lightColor) {
        // colorByDepth = 'var(--gray5)'
        colorByDepth = 'rgba(194, 194, 194, 0.5)'
      } else {
        colorByDepth = 'var(--gray2)'
      }
      break
    case 2:
      if (lightColor) {
        // colorByDepth = '#3DCC91'
        colorByDepth = 'rgba(61, 204, 145, 0.5)'
      } else {
        colorByDepth = 'var(--gray3)'
      }
      break
    case 3:
      if (lightColor) {
        // colorByDepth = '#FFB366'
        colorByDepth = 'rgba(255, 179, 102, 0.5)'
      } else {
        colorByDepth = 'var(--gray4)'
      }
      break
    case 4:
      if (lightColor) {
        // colorByDepth = 'var(--primary5)'
        colorByDepth = 'rgba(213, 247, 173, 0.5)'
      } else {
        colorByDepth = 'var(--gray5)'
      }
      break
    case 5:
      if (lightColor) {
        // colorByDepth = '#FF7373'
        colorByDepth = 'rgba(255, 115, 115, 0.5)'
      } else {
        colorByDepth = 'var(--gray6)'
      }
      break
    case 0:
      if (lightColor) {
        // colorByDepth = '#48AFF0'
        colorByDepth = 'rgba(72, 175, 240, 0.5)'
      } else {
        colorByDepth = 'var(--gray7)'
      }
      break
    default:
      if (lightColor) {
        // colorByDepth = 'var(--gray8)'
        colorByDepth = 'rgba(171, 171, 171, 0.5)'
      } else {
        colorByDepth = 'var(--gray8)'
      }
      break
  }

  return colorByDepth
}

export function getColorBackgroundClassByDepth (depth) {
  switch (depth) {
    case 1:
      return 'darkBackground'
    case 2:
      return 'greenBackground'
    case 3:
      return 'orangeBackground'
    case 4:
      return 'purpleBackground'
    case 5:
      return 'redBackground'
    case 0:
      return 'blueBackground'
    default:
      return 'darkBackground'
  }
}

export function argMin (array) {
  return [].map.call(array, (x, i) => [x, i]).reduce((r, a) => (a[0] < r[0] ? a : r))[1]
}

export function comboTextByOS (combo) {
  return combo ? combo.replace('mod', ctrlOrCmd) : ''
}

export const showToastByMenuDirection = showToastByMenuDirectionBase

let savingCount = 0

export const handleErrorInCatch = (e, where) => {
  showToastByMenuDirection({
    message: i18next.t('Something went wrong and the data was not saved properly. Sorry.'),
    icon: 'error',
    intent: 'danger',
    action: {
      icon: 'refresh',
      text: 'Reload',
      onClick: () => {
        location.reload()
      }
    }
  })
  globalOnDemandVars.set_saveState('failed')
  savingCount -= 1

  if (process.env.NODE_ENV !== 'production') {
    console.tron.display({
      name: 'handleErrorInCatch',
      preview: where,
      value: {
        e, where
      }
      // , important: true
    })
  }

  throw (e)
}

export const savingStart = () => {
  savingCount += 1
  globalOnDemandVars.set_saveState('saving')
}
export const savingEnd = () => {
  savingCount -= 1
  if (savingCount === 0) {
    globalOnDemandVars.set_saveState('saved')
  }
}

export const JSONParserForMosaicValue = (MosaicValueJSON) => {
  if (MosaicValueJSON === 'null') {
    return null
  }
  const parsed = JSON.parse(MosaicValueJSON)
  if (parsed && parsed.direction) {
    return parsed
  } else {
    return MosaicValueJSON
  }
}

export function reorderForBnD (list, startIndex, endIndex) {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export function moveForBnD (source, destination, droppableSource, droppableDestination) {
  const sourceClone = Array.from(source)
  const destClone = Array.from(destination)
  const [removed] = sourceClone.splice(droppableSource.index, 1)

  destClone.splice(droppableDestination.index, 0, removed)

  const result = {}
  result['source'] = sourceClone
  result['destination'] = destClone

  return result
}

const focusDetectObject = {
  node: false,
  window1: false,
  window2: false,
  sidebar: false,
  chatMemoInput: false,
  chatMemoDisplay: false
}
export function setGuideType (newType, boolean) {
  focusDetectObject[newType] = boolean

  if (boolean) {
    if (focusDetectObject.node) {
      globalOnDemandVars.set_guideType('node')
    } else if (focusDetectObject.window1) {
      globalOnDemandVars.set_guideType('window1')
    }

    if (focusDetectObject.chatMemo) {
      globalOnDemandVars.set_guideType('chatMemo')
    }
  }
}

export function openNewWindowAtWindow2 (newId) {
  const isPixelMode = globalOnDemandVars.isPixelMode()
  const currentMosaicValue = isPixelMode ? globalOnDemandVars.mosaicValueForPixelMode() : globalOnDemandVars.mosaicValue()
  let nextMosaicValue
  if (currentMosaicValue) {
    const path = getPathToCorner(currentMosaicValue, Corner.TOP_LEFT)
    const parent = getNodeAtPath(currentMosaicValue, dropRight(path))
    const destination = getNodeAtPath(currentMosaicValue, path)
    const direction = parent ? getOtherDirection(parent.direction) : 'row'
    const first = newId
    const second = destination
    // let first
    // let second
    // if (direction === 'row' && !toLeft) {
    //   first = destination
    //   second = newId
    // } else {
    //   first = newId
    //   second = destination
    // }
    nextMosaicValue = updateTree(currentMosaicValue, [
      {
        path,
        spec: {
          $set: {
            direction,
            first,
            second
          }
        }
      }
    ])
  } else {
    nextMosaicValue = newId
  }
  if (isPixelMode) {
    globalOnDemandVars.set_mosaicValueForPixelMode(nextMosaicValue)
  } else {
    globalOnDemandVars.set_mosaicValue(nextMosaicValue)
  }
}

export function openNewWindowAtWindow2ByPath (path) {
  if (path) {
    const nextTileId = getIdForRenderTileInJSON(shortid.generate(), path)
    openNewWindowAtWindow2(nextTileId, sidebarDirection === 'left')
  } else {
    throw (new Error('path 가 없음'))
  }
}

export function findNodeAndPathByIdFromTreeData (id, treeData, pathSoFar) {
  if (id === 'rootNode') {
    return [{ title: globalOnDemandVars.topRootName() }, []]
  }

  // console.tron.display({
  //   name: 'findNodeById from tree data',
  //   preview: id,
  //   value: {
  //     id,
  //     treeData,
  //     pathSoFar
  //   }
  //   // , important: true
  // })
  const subfunctionForOneNode = (node, maybePath) => {
    // console.tron.display({
    //   name: 'findNodeById for one node',
    //   preview: node.id,
    //   value: {
    //     node,
    //     maybePath
    //   }
    //   // , important: true
    // })

    if (node.id === id) {
      maybePath.push(id)
      // console.tron.display({
      //   name: 'findNodeById 일치',
      //   preview: node.id,
      //   value: {
      //     id,
      //     node,
      //     maybePath
      //   },
      //   important: true
      // })
      return [node, maybePath]
    } else if (node.children && node.children.length) {
      // console.tron.display({
      //   name: 'findNodeById 불일치 & children',
      //   preview: node.id,
      //   value: {
      //     id,
      //     node,
      //     maybePath,
      //     children: node.children
      //   },
      //   important: true
      // })

      return findNodeAndPathByIdFromTreeData(id, node.children, Array.from(maybePath).concat(node.id))
    } else {
      // console.tron.display({
      //   name: 'findNodeById 불일치 & NO children',
      //   preview: node.id,
      //   value: {
      //     id,
      //     node,
      //     maybePath
      //   },
      //   important: true
      // })

      return false
    }
  }

  for (let i = 0; i < treeData.length; i++) {
    const currentNode = treeData[i]

    const result = subfunctionForOneNode(currentNode, pathSoFar || [])
    if (result) {
      // console.tron.display({
      //   name: 'findNodeById return result',
      //   preview: id,
      //   value: {
      //     id,
      //     result
      //   },
      //   important: true
      // })

      return result
    }
  }
}

export function findPathByIdFromTreeData (id, treeData, pathSoFar) {
  if (id === 'rootNode') {
    return []
  }

  const subfunctionForOneNode = (node, maybePath) => {
    if (node.id === id) {
      maybePath.push(id)
      return [node, maybePath]
    } else if (node.children) {
      return findNodeAndPathByIdFromTreeData(id, node.children, Array.from(maybePath).concat(node.id))
    } else {
      return false
    }
  }

  for (let i = 0; i < treeData.length; i++) {
    const currentNode = treeData[i]
    const result = subfunctionForOneNode(currentNode, pathSoFar || [])
    if (result) {
      return result
    }
  }
}

export function getNodeById (id, treeData) {
  const subfunctionForOneNode = (node) => {
    if (id === 'rootNode') {
      return { title: '', id: 'rootNode', children: treeData }
    }

    if (node.id === id) {
      return node
    } else if (node.children) {
      return getNodeById(id, node.children)
    } else {
      return false
    }
  }

  let result
  for (let i = 0; i < treeData.length; i++) {
    const currentNode = treeData[i]
    result = subfunctionForOneNode(currentNode)
    if (result) {
      break
    }
  }
  if (result) {
    return result
  }
}

export function getParentNodeById (id, treeData) {
  const subfunctionForOneNode = (node) => {
    if (id === 'rootNode') {
      return null
    }

    if (node.children && node.children.some((childNode) => childNode.id === id)) {
      return node
    } else if (node.children) {
      return getParentNodeById(id, node.children)
    } else {
      return false
    }
  }

  let result
  for (let i = 0; i < treeData.length; i++) {
    const currentNode = treeData[i]
    result = subfunctionForOneNode(currentNode)
    if (result) {
      break
    }
  }
  if (result) {
    return result
  }
}

export function getBrotherNodesById (id, treeData) {
  if (!treeData || !treeData.length) {
    return false
  } else if (treeData.some((node) => (node.id === id))) {
    return treeData
  } else {
    return treeData
      .map((node) => (getBrotherNodesById(id, node.children)))
      .filter((maybeNodeChildren) => (!!maybeNodeChildren))[0]
  }
}

// -----------------------------------------------------------------------------------------------------------

export function truncateDefault (string, shorter, shortershorter) {
  return truncate(string, { length: shortershorter ? 10 : (shorter ? 20 : 30), separator: '\n' })
}

export function getET1Index (treeIndex) {
  return 2 * treeIndex + 2
}

export function getET2Index (treeIndex) {
  return 2 * treeIndex + 3
}

export function heightPadding (height) {
  return height + 1
}
// -----------------------------------------------------------------------------------------------------------

export function withSafeFocused (callBack) {
  return (...args) => {
    if (globalOnDemandVars.safeFocused()) {
      // console.tron.log('onMoveNode 전 safeFocused true')
      globalOnDemandVars.set_safeFocused(false)
      try {
        defer(() => {
          callBack(...args)
        })
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.tron.error(e)
        }
        globalOnDemandVars.set_safeFocused(true)
      }
    } else {
      // console.tron.log('onMoveNode 전 safeFocused false')
      return false
    }
  }
}

// -----------------------------------------------------------------------------------------------------------

export function copyTextToClipBoard (text) {
  try {
    return navigator.clipboard.writeText(text)
  } catch (e) {
  }
}

export async function pasteTextFromClipBoard (element) {
  try {
    let clipTextReturn
    await navigator.clipboard.readText().then(clipText => {
      clipTextReturn = clipText
      insertTextAtCursor(element, clipText)
    })
    return clipTextReturn
  } catch (e) {
  }
}

export function subtractForPath (longPath, shortPath) {
  // console.tron.display({
  //   name: 'subtractForPath',
  //   value: {
  //     longPath,
  //     shortPath
  //   },
  //   preview: longPath
  //   // , important: true
  // })
  const shortPathLength = shortPath.length

  if (shortPathLength === -1) {
    return {
      length: longPath.length + 1
    }
  }

  let result
  if (shortPathLength) {
    // const lastElementOfShortPath = shortPath[shortPathLength - 1]
    // result = longPath.slice(shortPathLength).map((v) => (v - (lastElementOfShortPath + 1)))
    result = longPath.slice(shortPathLength)
  } else {
    result = longPath
  }
  return result
}

export function reverseSubtractForPath (subtractedPath, subtractingPath) {
  if (subtractingPath.length === -1) {
    return null
  }
  let result
  if (subtractingPath.length) {
    // const lastElementOfSubtractingPath = subtractingPath[subtractingPath.length - 1]
    // result = subtractingPath.concat(subtractedPath.map((v) => (v + (lastElementOfSubtractingPath + 1))))
    result = subtractingPath.concat(subtractedPath)
  } else {
    result = subtractedPath
  }
  return result
}

export function inclusionCheckForPath (shortPath, longPath) {
  if (shortPath.length === -1) {
    return true
  }

  return shortPath.every((v, i) => (v === longPath[i]))
}

// -----------------------------------------------------------------------------------------------------------

// We'll copy the properties below into the mirror div.
// Note that some browsers, such as Firefox, do not concatenate properties
// into their shorthand (e.g. padding-top, padding-bottom etc. -> padding),
// so we have to list every single property explicitly.
var properties = [
  'direction', // RTL support
  'boxSizing',
  'width', // on Chrome and IE, exclude the scrollbar, so the mirror div wraps exactly as the textarea does
  // 'height',
  // 'overflowX',
  // 'overflowY', // copy the scrollbar for IE

  // 'borderTopWidth',
  // 'borderRightWidth',
  // 'borderBottomWidth',
  // 'borderLeftWidth',
  // 'borderStyle',

  // 'paddingTop',
  // 'paddingRight',
  // 'paddingBottom',
  // 'paddingLeft',

  // https://developer.mozilla.org/en-US/docs/Web/CSS/font
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',

  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration', // might not make a difference, but better be safe

  'letterSpacing',
  'wordSpacing',

  'tabSize',
  'MozTabSize'

]

var isBrowser = (typeof window !== 'undefined')
var isFirefox = (isBrowser && window.mozInnerScreenX != null)

// The mirror div will replicate the textarea's style
var div = document.createElement('div')
div.id = 'input-textarea-caret-position-mirror-div'
document.body.appendChild(div)
var style = div.style
style.whiteSpace = 'pre-wrap'
style.position = 'absolute' // required to return coordinates properly
style.top = '0'

export function getCaretCoordinates (element, position, options) {
  if (!isBrowser) {
    throw new Error('textarea-caret-position#getCaretCoordinates should only be called in a browser')
  }

  var debug = (options && options.debug) || false
  if (debug) {
    var el = document.querySelector('#input-textarea-caret-position-mirror-div')
    if (el) el.parentNode.removeChild(el)
  }

  // // The mirror div will replicate the textarea's style
  // var div = document.createElement('div')
  // div.id = 'input-textarea-caret-position-mirror-div'
  // document.body.appendChild(div)

  // var style = div.style
  var computed = window.getComputedStyle ? window.getComputedStyle(element) : element.currentStyle // currentStyle for IE < 9
  var isInput = element.nodeName === 'INPUT'

  // Default textarea styles
  // style.whiteSpace = 'pre-wrap'
  if (!isInput) { style.wordWrap = 'break-word' } // only for textarea-s

  // Position off-screen
  // style.position = 'absolute' // required to return coordinates properly
  if (!debug) { style.visibility = 'hidden' } // not 'display: none' because we want rendering

  // Transfer the element's properties to the div
  properties.forEach(function (prop) {
    if (isInput && prop === 'lineHeight') {
      // Special case for <input>s because text is rendered centered and line height may be != height
      if (computed.boxSizing === 'border-box') {
        var height = parseInt(computed.height)
        var outerHeight =
            parseInt(computed.paddingTop) +
            parseInt(computed.paddingBottom) +
            parseInt(computed.borderTopWidth) +
            parseInt(computed.borderBottomWidth)
        var targetHeight = outerHeight + parseInt(computed.lineHeight)
        if (height > targetHeight) {
          style.lineHeight = height - outerHeight + 'px'
        } else if (height === targetHeight) {
          style.lineHeight = computed.lineHeight
        } else {
          style.lineHeight = 0
        }
      } else {
        style.lineHeight = computed.height
      }
    } else {
      style[prop] = computed[prop]
    }
  })

  if (isFirefox) {
    // Firefox lies about the overflow property for textareas: https://bugzilla.mozilla.org/show_bug.cgi?id=984275
    if (element.scrollHeight > parseInt(computed.height)) { style.overflowY = 'scroll' }
  } else {
    style.overflow = 'hidden' // for Chrome to not render a scrollbar; IE keeps overflowY = 'scroll'
  }

  const stringBeforeCaret = element.value.substring(0, position)
  div.textContent = stringBeforeCaret
  // The second special handling for input type="text" vs textarea:
  // spaces need to be replaced with non-breaking spaces - http://stackoverflow.com/a/13402035/1269037
  if (isInput) { div.textContent = div.textContent.replace(/\s/g, '\u00a0') }

  var span = document.createElement('span')
  // Wrapping must be replicated *exactly*, including when a long word gets
  // onto the next line, with whitespace at the end of the line before (#7).
  // The  *only* reliable way to do that is to copy the *entire* rest of the
  // textarea's content into the <span> created at the caret position.
  // For inputs, just '.' would be enough, but no need to bother.
  const stringAfterCaret = element.value.substring(position)
  span.textContent = stringAfterCaret || '.' // || because a completely empty faux span doesn't render at all
  div.appendChild(span)

  const height = parseInt(computed['lineHeight'])
  const top = span.offsetTop
  var coordinates = {
    // top: span.offsetTop + parseInt(computed['borderTopWidth']),
    // left: span.offsetLeft + parseInt(computed['borderLeftWidth']),
    top: top,
    bottom: div.scrollHeight - top - height,
    left: span.offsetLeft,
    height: height,
    stringAfterCaret,
    stringBeforeCaret
  }

  if (debug) {
    span.style.backgroundColor = '#aaa'
  } else {
    // document.body.removeChild(div)
    div.removeChild(span)
  }

  return coordinates
}

// -----------------------------------------------------------------------------------------------------------

export function getProperties (object) {
  const resultObject = {}
  for (let key of Object.keys(object)) {
    if (typeof object[key] !== 'function') {
      resultObject[key] = object[key]
    }
  }
  return resultObject
}

// -----------------------------------------------------------------------------------------------------------

export const notSavedInSharing = throttle(() => {
  showToastByMenuDirection({
    message: i18next.t('Any changes you make here will not be reflected in the original data'),
    icon: 'warning-sign'
  })
}, 60000, { leading: true, trailing: false })

export const notSavedInAnonymous = throttle(() => {
  showToastByMenuDirection({
    message: i18next.t('Data for guest users will not be saved'),
    icon: 'warning-sign',
    intent: 'warning',
    action: {
      icon: 'new-person',
      text: 'Sign Up',
      onClick: () => {
        globalOnDemandVars.set_forceSignInPage(true)
      }
    }
  })
}, 60000, { leading: true, trailing: false })

export const notSavedInPlayground = () => {
  showToastByMenuDirection({
    message: i18next.t('Any changes you make in Playground will not be saved'),
    icon: 'warning-sign',
    intent: 'warning',
    className: 'yellowBackground pixelFont'
  })
}
// -----------------------------------------------------------------------------------------------------------

export function firstLetterCapitalize (string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

// -----------------------------------------------------------------------------------------------------------

export function toggleKnowledgeTreeMode () {
  if (globalOnDemandVars.knowledgeTreeMode() === 'reference') {
    globalOnDemandVars.set_knowledgeTreeMode('memorizing')
    showToastByMenuDirection({
      intent: 'primary',
      message: i18next.t('Organize for Memorizing'),
      icon: 'predictive-analysis'
    })
    return 'memorizing'
  } else {
    globalOnDemandVars.set_knowledgeTreeMode('reference')
    showToastByMenuDirection({
      intent: 'primary',
      message: i18next.t('Organize for Reference'),
      icon: 'map-create'
    })
    return 'reference'
  }
}

export const altEnter = () => {
  try {
    const trial = globalOnDemandVars.focusToLastNode()()
    if (!trial) {
      if (globalOnDemandVars.focusToNodeOfCurrentWindow()) {
        globalOnDemandVars.focusToNodeOfCurrentWindow()()
      } else {
        globalOnDemandVars.focusToNodeOfLastOpenedWindow()()
      }
    }
  } catch (e) {

  }
}

export function textToGeneralizedTextElement (text, shorter = true) {
  return text ? truncateDefault(text, shorter) : <span css={css`color: var(--gray6)`}>Blank</span>
}

export function shouldNodeDeleted (node) {
  return (!node.title && !node.subtitle && (!node.children || !node.children.length))
}

export function getHighlightClass (option) {
  switch (Number(option)) {
    case 1:
      return 'highlight1'
    case 2:
      return 'highlight2'
    case 3:
      return 'highlight3'
    case 4:
      return 'highlight4'
    case 5:
      return 'highlight5'
    case 6:
      return 'highlight6'
    default:
      return 'highlight1'
  }
}

// -----------------------------------------------------------------------------------------------------------

export function isLastChild (targetPath, ancestorNode) {
  let resultBoolean = true
  const length = targetPath.length
  let inspectedNode = ancestorNode
  for (let i = 0; i < length; i++) {
    const targetId = targetPath[i]
    const inspectedNodeChildren = inspectedNode.children
    const inspectedNodeLength = inspectedNodeChildren.length
    const inspectedNodeLastChild = inspectedNodeChildren[inspectedNodeLength - 1]

    if (inspectedNodeLastChild.id !== targetId) {
      resultBoolean = false
      break
    } else {
      inspectedNode = inspectedNodeLastChild
    }
  }
  return resultBoolean
}

// -----------------------------------------------------------------------------------------------------------

export function decorateNode (nodeId, name, option, data = {}) {
  let node, path
  if (data.node && data.path) {
    node = data.node
    path = data.path
  } else {
    const nodeObject = new NodeById(nodeId)
    const nodeAndPath = nodeObject.getNodeAndPath()
    node = nodeAndPath[0]
    path = nodeAndPath[1]
  }

  if (process.env.NODE_ENV !== 'production') {
    console.tron.display({
      name: 'decorateNode',
      preview: node.title,
      value: {
        nodeId,
        node,
        path,
        name,
        option
      }
      // , important: true
    })
  }

  const getTreeData = !globalOnDemandVars.isPixelMode() ? globalOnDemandVars.globalTreeData : globalOnDemandVars.pixelTreeData
  const setTreeData = !globalOnDemandVars.isPixelMode() ? globalOnDemandVars.set_globalTreeData : globalOnDemandVars.set_pixelTreeData

  const prevDecorating = node.decorating || {}
  const newDecorating = produce(prevDecorating, draft => {
    if (option) {
      const prevObject = prevDecorating[name]
      if (option === 'default') {
        if (!prevObject) {
          draft[name] = {
            boolean: true,
            option: 1
          }
        } else {
          draft[name] = {
            ...prevObject,
            boolean: !prevObject.boolean
          }
        }
      } else {
        if (!prevObject) {
          draft[name] = {
            boolean: true,
            option
          }
        } else {
          if (prevObject.boolean) {
            if (prevObject.option !== option) {
              draft[name] = {
                boolean: true,
                option
              }
            } else {
              draft[name] = {
                boolean: false,
                option
              }
            }
          } else {
            draft[name] = {
              boolean: true,
              option
            }
          }
        }
      }
      if (typeof data.boolean !== 'undefined') {
        draft[name].boolean = data.boolean
      }
    } else {
      draft[name] = !prevDecorating[name]
    }
  })

  const newNode = {
    ...node,
    // expanded: !node.expanded,
    decorating: newDecorating
  }
  // -----------------------------------------------------------------------------------------------------------

  if (name === 'boxHighlight') {
    globalOnDemandVars.boxHighlights()[nodeId] = newDecorating.boxHighlight
  }

  // -----------------------------------------------------------------------------------------------------------
  const prevTreeData = getTreeData()

  let parentNode = null
  if (path.length > 1) {
    parentNode = getNodeAtPathForTree({
      treeData: prevTreeData,
      path: path.slice(0, -1),
      getNodeKey: getNodeKey,
      ignoreCollapsed: false
    })
    parentNode = { ...parentNode }
  }

  const newTreeData = changeNodeAtPath({
    treeData: prevTreeData,
    path: path,
    newNode: newNode,
    getNodeKey: getNodeKey,
    ignoreCollapsed: false
  })

  const changeGlobally = () => {
    setTreeData(newTreeData)
    globalOnDemandVars.set_changeType('node')
    globalOnDemandVars.set_changedPath(path)
    globalOnDemandVars.set_changedPrevPath(path)
    globalOnDemandVars.set_changedNode(newNode)
    globalOnDemandVars.set_changedParentNode(parentNode)
  }

  if (process.env.NODE_ENV !== 'production') {
    console.tron.display({
      name: 'decorating',
      value: {
        node,
        newNode,
        prevDecorating,
        newDecorating,
        parentNode,
        path
      }
      // , important: true
    })
  }

  globalOnDemandVars.set_changedTreeId('notFromTree')
  changeGlobally()

  const setTreeChangeDetect = globalOnDemandVars.isPixelMode() ? globalOnDemandVars.set_treeChangeDetectForPixelMode : globalOnDemandVars.set_treeChangeDetect
  const getCurrentTreeChangeDetect = globalOnDemandVars.isPixelMode() ? globalOnDemandVars.treeChangeDetectForPixelMode : globalOnDemandVars.treeChangeDetect
  setTreeChangeDetect(!getCurrentTreeChangeDetect())

  updateTreeNode(newNode.id, { decorating: newDecorating })
  // -----------------------------------------------------------------------------------------------------------

  undoManager.add({
    redo: () => {
      globalOnDemandVars.set_changedTreeId('notFromTree')
      changeGlobally()

      if (name === 'boxHighlight') {
        globalOnDemandVars.boxHighlights()[nodeId] = newDecorating.boxHighlight
      }

      // const getCurrentTreeChangeDetect = globalOnDemandVars.isPixelMode() ? globalOnDemandVars.treeChangeDetectForPixelMode : globalOnDemandVars.treeChangeDetect
      setTreeChangeDetect(!getCurrentTreeChangeDetect())

      updateTreeNode(newNode.id, { decorating: newDecorating })
    },
    undo: () => {
      globalOnDemandVars.set_changedTreeId('notFromTree')

      setTreeData(prevTreeData)
      globalOnDemandVars.set_changeType('node')
      globalOnDemandVars.set_changedPath(path)
      globalOnDemandVars.set_changedPrevPath(path)
      globalOnDemandVars.set_changedNode(node)
      globalOnDemandVars.set_changedParentNode(parentNode)

      if (name === 'boxHighlight') {
        globalOnDemandVars.boxHighlights()[nodeId] = prevDecorating.boxHighlight
      }

      // const getCurrentTreeChangeDetect = globalOnDemandVars.isPixelMode() ? globalOnDemandVars.treeChangeDetectForPixelMode : globalOnDemandVars.treeChangeDetect
      setTreeChangeDetect(!getCurrentTreeChangeDetect())

      updateTreeNode(newNode.id, { decorating: prevDecorating })
    }
  })
}

export function toggleDecorateNode (nodeId, name) {
  const nodeObject = new NodeById(nodeId)
  const [ node, path ] = nodeObject.getNodeAndPath()
  const configObject = decoratingConfig[name]

  if (!configObject.options) {
    decorateNode(node.id, name, null, { node, path })
  } else {
    const currentDecoratingObject = node.decorating[name]

    if (!currentDecoratingObject) {
      decorateNode(node.id, name, 1, { node, path })
    } else if (!currentDecoratingObject.boolean) {
      decorateNode(node.id, name, currentDecoratingObject.option, { node, path })
    } else if ((currentDecoratingObject.option >= 1) && (currentDecoratingObject.option < configObject.lastOptionNumber)) {
      decorateNode(node.id, name, currentDecoratingObject.option + 1, { node, path })
    } else if (currentDecoratingObject.option === configObject.lastOptionNumber) {
      decorateNode(node.id, name, 1, { node, path, boolean: false })
    } else {
      decorateNode(node.id, name, 1, { node, path })
    }
  }
}

// -----------------------------------------------------------------------------------------------------------

const RegExpForLastSpace = /\s$/
const RegExpForFirstSpace = /^\s/
export const insertStringAtNode = (string) => {
  if (globalOnDemandVars.guideType() !== 'node') {
    globalOnDemandVars.focusToLastNode()()
    if (globalOnDemandVars.guideType() !== 'node') {
      return false
    }
  }

  const selection = globalOnDemandVars.getNodeSelection()()
  const nodeValue = globalOnDemandVars.getNodeStateValue()()

  const caretStart = selection.start
  const caretEnd = selection.end
  const caretPosition = caretEnd
  const stringBeforeCaret = nodeValue.substring(0, caretPosition)
  const stringAfterCaret = nodeValue.substring(caretPosition)

  let newStart = stringBeforeCaret
  let newEnd = stringAfterCaret

  if (stringBeforeCaret && !RegExpForLastSpace.test(stringBeforeCaret)) {
    newStart = newStart + ' '
  } else {
    RegExpForLastSpace.lastIndex = 0
  }
  if (stringAfterCaret && !RegExpForFirstSpace.test(stringAfterCaret)) {
    newEnd = ' ' + newEnd
  } else {
    RegExpForFirstSpace.lastIndex = 0
  }

  const newValue = newStart + string + newEnd
  globalOnDemandVars.setNodeStateValue()(newValue)

  const newCaretPosition = caretPosition + (newStart.length - stringBeforeCaret.length) + (string.length)
  globalOnDemandVars.focusToLastNode()()
  defer(() => {
    globalOnDemandVars.setNodeSelection()(newCaretPosition, newCaretPosition)
  })
  // -----------------------------------------------------------------------------------------------------------
  undoManager.add({
    redo: () => {
      globalOnDemandVars.setNodeStateValue()(newValue)
      defer(() => {
        globalOnDemandVars.setNodeSelection()(newCaretPosition, newCaretPosition)
      })
    },
    undo: () => {
      globalOnDemandVars.setNodeStateValue()(nodeValue)
      defer(() => {
        globalOnDemandVars.setNodeSelection()(caretStart, caretEnd)
      })
    }
  })
}
